/**
 * @author Brendan Magee
 * @desc Form for users to set their default notification target (Slack or email)
 * @example <default-notification-target user="user"></default-notification-target>
 */

import template from './defaultNotificationTarget.html';

function DefaultNotificationTargetController(
  $translate,
  $log,
  peopleService,
  timezoneService,
  Integration
) {
  this.$onInit = () => {
    this.targetData = {
      email: {
        type: 'email',
        address: this.user.email,
        timezone: timezoneService.userTimezone,
        htmlEnabled: true
      },
      slack: {
        type: 'slack',
        timezone: timezoneService.userTimezone,
        integrationID: null,
        username: null,
        channel: null
      }
    };

    this.targets = Object.keys(this.targetData).map((type) => ({
      type,
      name: type === 'email' ? $translate.instant('EMAIL') : 'Slack'
    }));
    this.targetType = null;
    this.errorMessage = '';

    const { preferences = {} } = this.user;

    if (preferences.defaultNotificationTarget) {
      const { defaultNotificationTarget: target } = preferences;

      this.targetType = target.type;

      this.targetData[this.targetType] = target;
    }

    if (preferences.slackUsername && !this.targetData.slack.username) {
      this.targetData.slack.username = preferences.slackUsername;
      this.targetData.slack.channel = preferences.slackUsername;
    }

    Integration.getAllByType('slack').then(
      (integrations) => {
        this.integrations = integrations || [];
        const { integrationID } = this.targetData.slack;
        const validIntegration = integrations.find(
          (integration) => integration.id === integrationID
        );

        if (validIntegration) {
          this.targetData.slack.integrationID = validIntegration.id;
        } else {
          const [firstIntegration = {}] = integrations;
          this.targetData.slack.integrationID = firstIntegration.id;
        }
      },
      () => {
        this.dataRetrievalError = true;
        $log.error('Error getting Slack integrations');
      }
    );
  };

  this.setIntegrationID = (integration) => {
    this.targetData.slack.integrationID = integration.id;
    // Save the correct Slack workspace along with the username
    this.saveDefaultTarget();
  };

  this.saveDefaultTarget = () => {
    this.errorMessage = '';
    this.saving = true;
    const payload = this.targetData[this.targetType];

    const missing = [];
    Object.keys(payload).forEach((prop) => {
      if (!payload[prop]) {
        missing.push(prop);
      }
    });

    if (missing.length) {
      this.saving = false;
      this.targetType = null;
      this.errorMessage = $translate.instant(
        'EASY_NOTIFICATIONS.MISSING_DATA_ERROR',
        { fields: missing.join(', ') }
      );
      return;
    }

    peopleService.saveNotificationTarget(payload).then(
      () => {
        this.saving = false;
        if (this.parent && !this.parent.target) {
          this.parent.target = payload;
        }
      },
      (response) => {
        this.saving = false;
        this.errorMessage = $translate.instant(
          'EASY_NOTIFICATIONS.DEFAULT_TARGET_SAVE_ERROR'
        );

        const error =
          response.data && response.data.error ? response.data.error : response;
        $log.error(error);
      }
    );
  };
}

const defaultNotificationTarget = {
  template,
  bindings: {
    user: '<'
  },
  require: {
    parent: '?^^easyNotificationForm'
  },
  controller: 'DefaultNotificationTargetController'
};

const controllerArray = [
  '$translate',
  '$log',
  'peopleService',
  'timezoneService',
  'Integration',
  DefaultNotificationTargetController
];

export default function (ngModule) {
  ngModule.component('defaultNotificationTarget', defaultNotificationTarget);
  ngModule.controller('DefaultNotificationTargetController', controllerArray);
}
